import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { useSendOtpApi } from '@hero/api-hooks';
import { phoneSchema } from '@hero/schema';
import { useNav } from '../navigation/useNav';
import { setPhoneResponse } from './tokens';

// Uses the canonical `phoneSchema` (Saudi normalization + validation)
// so the number sent to `/auth/send-otp` matches what the backend
// expects — same schema the app-local hooks used before unification.
const phoneFormSchema = z.object({
  phone: phoneSchema,
});

export type UsePhoneNumberManagerOptions = {
  /**
   * Optional: override the default redirect after a successful OTP
   * send. Defaults to the shared `auth_verify_otp` route.
   */
  onSent?: () => void;
};

/**
 * Unified phone-number step of the signup wizard. Wraps
 * `react-hook-form` + `useSendOtpApi` and persists the phone response
 * via the shared `tokens.ts` store. Identical behavior across
 * customer-dashboard, carrier-dashboard, and admin-panel.
 *
 * See `docs/relations-v2/08-user-identity.md`.
 */
export function usePhoneNumberManager(options: UsePhoneNumberManagerOptions = {}) {
  const { t } = useTranslation(['toast']);
  const { navigate } = useNav();
  const { onSent } = options;

  const form = useForm<z.infer<typeof phoneFormSchema>>({
    resolver: zodResolver(phoneFormSchema),
    defaultValues: { phone: '' },
  });

  const { mutateAsync, isPending } = useSendOtpApi();

  const submit = useCallback(
    async (values: z.infer<typeof phoneFormSchema>) => {
      try {
        const response = await mutateAsync({ phone: values.phone });
        setPhoneResponse({
          isNewUser: response.isNewUser,
          otpId: response.otpId,
          phone: response.phone,
        });
        toast.success(t('toast:success.otpSent'), { id: 'auth-otp-flow' });
        if (onSent) onSent();
        else navigate('auth_verify_otp');
      } catch (e) {
        const message = e instanceof Error ? e.message : t('toast:error.otpSend');
        toast.error(message);
      }
    },
    [mutateAsync, navigate, onSent, t],
  );

  return {
    form,
    isPending,
    onSubmit: form.handleSubmit(submit),
  };
}
